import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Carousel } from 'antd/es';

import { months } from '@app/components/LocalCommon/Constants/Months';
import LinkMore from '@app/components/LocalCommon/LinkMore/LinkMore';
import CustomAvatar from '@app/components/UI/CustomAvatar/CustomAvatar';
import CarrotDownSVG from '@app/images/CarrotDownSVG';
import imagePlaceholder from '@app/images/image-placeholder.png';
import { getImagePath } from '@app/objects/Image';
import { VetTestimonialsRes } from '@app/objects/VetTestimonialsRes';

export interface Date {
	month: string;
	day: number;
	year: number;
}

interface TesAuthorCreditsProps {
	firstName?: string;
	lastName?: string;
	city?: string;
	state?: string;
	date?: Date;
	isShowName?: boolean;
}

export const VetAvatar = ({ vetAvatar = imagePlaceholder, vetName }) => {
	return (
		<div className="vet-avatar">
			<img
				src={vetAvatar}
				alt="vet"
				onError={(e: any) => {
					e.target.onerror = null;
					e.target.src = imagePlaceholder;
				}}
			/>
			<span>{vetName}</span>
		</div>
	);
};

export const TesAuthorCredits: React.FC<TesAuthorCreditsProps> = ({
	firstName,
	lastName,
	city,
	state,
	date,
	isShowName = true,
}) => {
	return (
		<div className="tes-author-credits">
			<div className="tes-author-credits__personal">
				{isShowName && (firstName || lastName) && (
					<div className="tes-author-credits__name">
						<span>{firstName}</span>
						{' '}
						<span>{lastName}</span>
					</div>
				)}
				{(city || state) && (
					<div className="tes-author-credits__location">
						<span>{city}</span>
						{city && state && <span>, </span>}
						{state && <span>{state}</span>}
					</div>
				)}
			</div>

			<div className="tes-author-credits__date">
				{date && date.month && (
					<>
						<span>{date?.month}</span>
						{' '}
						<span>{date?.day}</span>
						<span>, </span>
						<span>{date?.year}</span>
					</>
				)}
			</div>
		</div>
	);
};

const VetTesCard: React.FC<{ tes: VetTestimonialsRes; navCollapse: boolean; }> = ({ tes, navCollapse }) => {
	const date = new Date(tes.dateCreated);
	const dateValues = {
		month: months[date.getMonth()],
		day: date.getDate(),
		year: date.getFullYear(),
	};

	const [imgSrc, setImgSrc] = React.useState(tes.avatar);

	const navigate = useNavigate();

	const petImgHeight = 400;
	const petImgWidth = 280;
	React.useEffect(() => {
		setImgSrc(tes.avatar);
	}, []);

	const imageAltCapture = `
		Lap of Love Veterinary Hospice ${tes.city ? tes.city : ''}, 
		${tes.state ? tes.state : ''} Testimonial for Dr. ${tes.user.firstName} ${tes.user.lastName} 
		${tes.firstName ? `by ${tes.firstName} ${tes.lastName}` : ''}`;

	const openTestimonialCard = () => {
		navigate(`/veterinarian-testimonials/${tes.id}`);
	};

	return (
		<div className="vet-tes-card__wrapper" onClick={openTestimonialCard}>
			<div className="vet-tes-card">
				<VetAvatar vetAvatar={tes.user.avatar} vetName={`Dr. ${tes.user.firstName}`} />

				{!navCollapse ? (
					<p className="vet-tes-card__text">{tes.message}</p>
				) : tes.avatar ? (
					<div>
						<Carousel slidesToShow={1} dots={false} adaptiveHeight>
							<div className="vet-tes-card_swipe">
								<p className="vet-tes-card__text">{tes.message}</p>
							</div>

							<div className="vet-tes-card_swipe-img-wrapper">
								<img
									src={imgSrc !== imagePlaceholder ? getImagePath(imgSrc, petImgHeight, petImgWidth) : imgSrc}
									onError={() => setImgSrc(imagePlaceholder)}
									alt={imageAltCapture}
								/>
							</div>
						</Carousel>
					</div>
				) : (
					<p className="vet-tes-card__text">{tes.message}</p>
				)}

				<TesAuthorCredits firstName={tes.firstName} lastName={tes.lastName} city={tes.city} state={tes.state} date={dateValues} />
				<div className="vet-tes-card__look-more">
					{tes.user.firstName && (
						<>
							{tes.user.areaUrl && (
								<LinkMore
									linkMoreCaption={`View Dr. ${tes.user.firstName}'s biography`}
									linkMoreLink={`/find-a-vet/${tes.user.area.state?.replace(' ', '-')}/${
										tes.user.areaUrl
									}/veterinarians?veterinarian=${tes.user.id}`}
								/>
							)}

							{tes.petMemorial && (
								<LinkMore
									linkMoreCaption={`View ${tes.petMemorial.petName}'s memorial`}
									linkMoreLink={`/pet-memorial/${tes.petMemorial.id}`}
								/>
							)}
						</>
					)}
				</div>
				{navCollapse && tes.avatar && (
					<div className="vet-tes-card_swipe-pointer">
						<CarrotDownSVG />
					</div>
				)}
			</div>
			{tes.avatar && (
				<div className="vet-tes-card__pet-img">
					<img
						src={imgSrc !== imagePlaceholder ? getImagePath(imgSrc, petImgHeight, petImgWidth) : imgSrc}
						alt={imageAltCapture}
						onError={() => setImgSrc(imagePlaceholder)}
					/>
				</div>
			)}
		</div>
	);
};

export default VetTesCard;
